/**
 * The Desktop surface: the runtime's whole screen, and a way to drive it.
 *
 * The Browser surface streams the same display cropped to Chromium's viewport.
 * This one streams it uncropped, which is the only way to see anything the
 * agent opened *outside* the browser — a file manager, an editor, an installer
 * dialog — and the only way to interact with it.
 *
 * Every decision about what to show lives in `desktopPanelState.ts`, and every
 * decision about what a gesture *means* — above all whether the user is
 * watching or driving — lives in `desktopInputIntent.ts`. This file draws the
 * result and moves bytes; it deliberately holds no rule of its own, because a
 * rule in here can only be reached by a test through a real pointer.
 */
import type {
  BrowserDisplaySession,
  ComputerCommand,
  ScopedThreadRef,
} from "@cmmd-forge/contracts";
import { useCallback, useEffect, useRef, useState } from "react";

import { selectBrowserThreadState, useBrowserStateStore } from "~/browserStateStore";
import { ensureEnvironmentApi } from "~/environmentApi";
import { cn } from "~/lib/utils";

import { RemoteWebRtcBrowserSurface } from "../browser/RemoteWebRtcBrowserSurface";

import { DesktopStartPane } from "./DesktopStartPane";
import { DesktopSurfaceOverlay } from "./DesktopSurfaceOverlay";
import { type DesktopGesture, desktopInputIntent } from "./desktopInputIntent";
import {
  canStartDesktop,
  commandForKeyEvent,
  desktopPanelCopy,
  desktopPanelState,
  mapPointerToDisplay,
  mouseButtonFromEventButton,
  type DisplayPoint,
} from "./desktopPanelState";

export interface DesktopPanelProps {
  readonly threadRef: ScopedThreadRef;
  /** Opens the Browser surface, which is what starts the runtime display. */
  readonly onOpenBrowser: () => void;
}

interface VideoSize {
  readonly width: number;
  readonly height: number;
}

export function DesktopPanel(props: DesktopPanelProps) {
  const { onOpenBrowser, threadRef } = props;
  const { environmentId, threadId } = threadRef;
  /*
   * The session comes from the store rather than being opened here: the
   * desktop IS the display that a browser session creates, so a second opener
   * would race the Browser surface for the same runtime. Browser events are
   * subscribed at the connection level, so the ICE candidates below keep
   * arriving even while the Browser tab is unmounted.
   */
  const browserThreadState = useBrowserStateStore((state) =>
    selectBrowserThreadState(state.browserStateByThreadKey, threadRef),
  );
  const session = browserThreadState?.session ?? null;
  const browserId = session?.browserId ?? null;
  const sessionId = session?.sessionId ?? null;
  const userId = session?.userId ?? null;
  const tabId = session?.activeTabId ?? null;
  const [videoSize, setVideoSize] = useState<VideoSize | null>(null);
  const [controlHeld, setControlHeld] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  /*
   * Fed back into the surface as `iceServers`. The attach answer carries the
   * TURN credentials this environment issued; a reconnect that forgets them
   * has no relay to fall back on, which is exactly the network where a relay
   * was the only thing that worked.
   */
  const [display, setDisplay] = useState<BrowserDisplaySession | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const pressOriginRef = useRef<DisplayPoint | null>(null);
  const commandQueueRef = useRef<Promise<void>>(Promise.resolve());

  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const state = desktopPanelState({
    hasSession: sessionId !== null,
    streamReady: videoSize !== null,
    controlHeld,
    starting,
  });
  const copy = desktopPanelCopy(state);
  const controlling = state === "controlling";

  // Control is not something to keep holding across a stream that went away:
  // the next frame may be a different screen than the one the user aimed at.
  useEffect(() => {
    if (videoSize === null) setControlHeld(false);
  }, [videoSize]);

  // Taking control from the button leaves focus on the button, so the first
  // keystroke would go to the page instead of the display. Move it here.
  useEffect(() => {
    if (controlHeld) surfaceRef.current?.focus();
  }, [controlHeld]);

  /*
   * Starting the display from here rather than sending the user to the Browser
   * surface.
   *
   * This calls the SAME openSession the Browser panel calls, deliberately. The
   * server keys a session on the THREAD and resolves an existing in-memory or
   * persisted one before creating anything — "One browser session per thread.
   * The thread is the canonical key" (browser/Layers/Manager.ts) — so if the
   * Browser surface already opened one, this collapses onto it instead of
   * racing it. That dedupe is why a second opener is safe, and it is also why
   * no new abstraction is introduced here: the seam that owns the invariant
   * already exists on the server.
   */
  const startDesktop = useCallback(() => {
    const browser = ensureEnvironmentApi(environmentId).browser;
    if (!browser) {
      setStartError("This Environment does not expose a display.");
      return;
    }
    setStartError(null);
    setStarting(true);
    browser
      .openSession({ threadId, environmentId })
      .then((snapshot) => {
        useBrowserStateStore.getState().setSessionSnapshot(threadRef, snapshot);
      })
      .catch((error: unknown) => {
        // Tagged RPC errors can arrive with an empty `message` (the shipped
        // BrowserError bug); an empty string here renders an invisible error
        // and the click reads as a no-op. Blank never reaches the user.
        const message = error instanceof Error ? error.message : "";
        setStartError(message === "" ? "Could not start the desktop." : message);
      })
      .finally(() => {
        setStarting(false);
      });
  }, [environmentId, threadId, threadRef]);

  const send = useCallback(
    (command: ComputerCommand) => {
      const api = ensureEnvironmentApi(environmentId);
      const computer = api.computer;
      if (!computer) {
        setInputError("This environment does not expose a controllable display.");
        return;
      }
      // Serialized because X applies input in arrival order: a click that
      // overtakes the move that positioned it lands wherever the pointer
      // happened to be.
      commandQueueRef.current = commandQueueRef.current
        .then(() => computer.input({ threadId, environmentId, command }))
        .then(() => {
          setInputError(null);
        })
        .catch((error: unknown) => {
          setInputError(error instanceof Error ? error.message : "Desktop input failed.");
        });
    },
    [environmentId, threadId],
  );

  const pointTo = useCallback(
    (clientX: number, clientY: number): DisplayPoint | null => {
      const element = surfaceRef.current;
      if (!element || !videoSize) return null;
      const rect = element.getBoundingClientRect();
      return mapPointerToDisplay({
        pointerX: clientX - rect.left,
        pointerY: clientY - rect.top,
        elementWidth: rect.width,
        elementHeight: rect.height,
        videoWidth: videoSize.width,
        videoHeight: videoSize.height,
      });
    },
    [videoSize],
  );

  /**
   * The DOM half of an input: read the event, apply what the intent says.
   *
   * No condition of its own — every "should this happen" question is answered
   * in `desktopInputIntent`, so adding a gesture cannot accidentally skip the
   * watch/drive guard.
   */
  const applyIntent = useCallback(
    (event: React.SyntheticEvent<HTMLDivElement>, gesture: DesktopGesture) => {
      const intent = desktopInputIntent({ controlling, gesture });
      if (intent.preventDefault) event.preventDefault();
      if (intent.focusSurface) event.currentTarget.focus();
      // `undefined` means leave the stored origin alone; `null` means clear it.
      if (intent.pressOrigin !== undefined) pressOriginRef.current = intent.pressOrigin;
      if (intent.releaseControl) setControlHeld(false);
      if (intent.command) send(intent.command);
    },
    [controlling, send],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      applyIntent(event, { kind: "pointer_down", point: pointTo(event.clientX, event.clientY) });
    },
    [applyIntent, pointTo],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      applyIntent(event, {
        kind: "pointer_up",
        origin: pressOriginRef.current,
        point: pointTo(event.clientX, event.clientY),
        button: mouseButtonFromEventButton(event.button),
      });
    },
    [applyIntent, pointTo],
  );

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      applyIntent(event, { kind: "double_click", point: pointTo(event.clientX, event.clientY) });
    },
    [applyIntent, pointTo],
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      applyIntent(event, { kind: "context_menu" });
    },
    [applyIntent],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      applyIntent(event, { kind: "key_down", key: event.key, command: commandForKeyEvent(event) });
    },
    [applyIntent],
  );

  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      applyIntent(event, {
        kind: "wheel",
        point: pointTo(event.clientX, event.clientY),
        deltaX: event.deltaX,
        deltaY: event.deltaY,
      });
    },
    [applyIntent, pointTo],
  );

  if (sessionId === null) {
    return (
      <DesktopStartPane
        copy={copy}
        canStart={canStartDesktop(state)}
        starting={starting}
        startError={startError}
        onStart={startDesktop}
        onOpenBrowser={onOpenBrowser}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/*
       * No chrome above a live desktop.
       *
       * The runtime's own top bar already carries the Forge mark and a clock,
       * and its dock carries the apps — a second bar stacked above them is the
       * product drawing its own furniture on top of the desktop it is showing.
       * Control moves onto the surface as a floating pill, which is also the
       * only place a user looking at the screen is already looking.
       */}
      {inputError === null ? null : (
        <div
          data-testid="desktop-input-error"
          className="shrink-0 border-b border-border px-3 py-2 text-xs text-muted-foreground"
        >
          {inputError}
        </div>
      )}
      <div className="relative flex min-h-0 flex-1 items-center justify-center bg-background p-2">
        <DesktopSurfaceOverlay
          state={state}
          copy={copy}
          controlling={controlling}
          onControlChange={setControlHeld}
        />
        <div
          ref={surfaceRef}
          data-testid="desktop-surface"
          data-controlling={controlling ? "true" : "false"}
          role="application"
          aria-label="Runtime desktop"
          tabIndex={controlling ? 0 : -1}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onDoubleClick={handleDoubleClick}
          onContextMenu={handleContextMenu}
          onKeyDown={handleKeyDown}
          onWheel={handleWheel}
          className={cn("relative max-h-full w-full overflow-hidden rounded-md outline-none", {
            "cursor-none ring-2 ring-info": controlling,
            "ring-1 ring-border": state === "watching",
          })}
          style={
            videoSize ? { aspectRatio: `${videoSize.width} / ${videoSize.height}` } : undefined
          }
        >
          <RemoteWebRtcBrowserSurface
            active
            captureMode="desktop"
            environmentId={environmentId}
            threadId={threadId}
            browserId={browserId}
            sessionId={sessionId}
            tabId={tabId}
            userId={userId}
            remoteIceCandidatesByClientId={browserThreadState?.displayIceCandidatesByClientId}
            iceServers={display?.iceServers}
            onVideoSizeChange={setVideoSize}
            onDisplayUpdated={setDisplay}
            onError={(error: unknown) => {
              setInputError(error instanceof Error ? error.message : "Desktop stream failed.");
            }}
          />
        </div>
      </div>
    </div>
  );
}
